import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, FormGroup, FormLabel, FormControl, Image, ListGroup } from 'react-bootstrap'
import Confirm from '@severed-links/common.confirm'
import TextareaAutosize from 'react-autosize-textarea'
import FontAwesomeIcon from '@severed-links/common.font-awesome-icon'
import { toggleTicketModal, updateActiveTicket, saveTicket, addComment, deleteComment } from '@severed-links/common.redherrings-reducers/help'
import moment from 'moment-timezone'
import { orderBy, startsWith, endsWith } from 'lodash'
import * as s from './HelpCenter.scss'
import { img } from '@severed-links/common.redherrings-constants'
import FormCheck from 'react-bootstrap/FormCheck'
import Linkify from 'react-linkify'
import Alert from '@severed-links/common.alert'

const EditTicket = () => {

    const dispatch = useDispatch()
    const { activeTicket } = useSelector(state => state.help)
    const [comment, setComment] = useState('')
    const [isMounted, setMounted] = useState(false)

    useEffect(() => { setMounted(true) }, [])

    const handleTextChange = e => dispatch(updateActiveTicket(e.target.name, e.target.value))
    const handleSelectChange = e => dispatch(updateActiveTicket(e.target.name, e.target.value))
    const handleSwitch = (el, checkedState) => dispatch(updateActiveTicket(el.target.name, checkedState))
    const handleCommentChange = e => setComment(e.target.value)
    const handleKeyPress = e => {
        if (e.key === 'Enter') {
            addCommentToTicket()
            e.preventDefault()
        }
    }

    const addCommentToTicket = () => {
        if (!comment || !comment.trim()) return
        dispatch(addComment({ helpTicketId: activeTicket._id, comment }))
        setComment('')
    }

    const doDeleteComment = commentId => dispatch(deleteComment(commentId))

    const doSaveTicket = () => {
        const ticket = {
            ...activeTicket,
            createDate: activeTicket._id ? moment(activeTicket.createDate).utc().toISOString() : moment().utc().toISOString(),
            updateDate: moment().utc().toISOString()
        }
        dispatch(toggleTicketModal(false))
        dispatch(saveTicket(ticket))
    }

    const getAvatarUrl = avatarUrl => img.BaseUrl + (!endsWith(img.BaseUrl, '/') && !startsWith(avatarUrl || img.NoImageAvailable, '/') ? '/' : '') + (avatarUrl || img.NoImageAvailable)

    if (!activeTicket) return null
    const _comments = orderBy(activeTicket.comments || [], ['createDate'], ['desc'])
    const _isExistingTicket = !!activeTicket._id
    const _ticketHeading = _isExistingTicket ? `Ticket #${activeTicket.ticketNumber || ''}` : 'New issue'
    const _ticketApp = (activeTicket.app || '').toLowerCase() === 'redherrings' ? 'REDHERRINGS' : activeTicket.app || 'REDHERRINGS'

    return (
        <div className={s.editor}>
            <section className={s.editorPanel}>
                <div className={s.editorHeader}>
                    <div className={s.editorIcon}><FontAwesomeIcon name='life-ring' /></div>
                    <div className={s.editorTitle}>
                        <div className={s.eyebrow}>{_ticketApp}</div>
                        <h3>{_ticketHeading}</h3>
                        {activeTicket.subject ? <p>{activeTicket.subject}</p> : null}
                    </div>
                </div>

                <div className={s.editorGrid}>
                    <FormGroup className={s.formField}>
                        <FormLabel>Site area</FormLabel>
                        <FormControl plaintext readOnly value={_ticketApp} />
                    </FormGroup>
                    <FormGroup className={s.formField}>
                        <FormLabel>Issue type</FormLabel>
                        <FormControl as='select'
                            onChange={e => handleSelectChange(e)}
                            name='ticketType'
                            required value={activeTicket.ticketType || ''}>
                            <option value=''>Select a type...</option>
                            <option value='Bug'>Something isn't working right</option>
                            <option value='Feature Request'>I have a feature request</option>
                            <option value='Comment'>I have a comment on the site</option>
                        </FormControl>
                    </FormGroup>
                </div>

                <FormGroup className={s.formField}>
                    <FormLabel>Subject</FormLabel>
                    <FormControl type='text' name='subject'
                        placeholder='Subject matter of issue...'
                        value={activeTicket.subject || ''}
                        required onChange={e => handleTextChange(e)} />
                </FormGroup>

                <FormGroup className={s.formField}>
                    <FormLabel>Description of issue</FormLabel>
                    <TextareaAutosize name='description' className={'form-control ' + s.descriptionInput}
                        placeholder='Describe your issue here...'
                        value={activeTicket.description || ''}
                        required onChange={e => handleTextChange(e)} rows={3}
                        onResize={() => {}} />
                </FormGroup>

                {isMounted && _isExistingTicket ?
                <div className={s.switchGrid}>
                    <label className={s.switchRow}>
                        <span>
                            <strong>Completed</strong>
                            <small>Mark the ticket resolved.</small>
                        </span>
                        <FormCheck id='help-ticket-completed' type='switch' onChange={e => handleSwitch(e, e.target.checked)}
                            name='isCompletedTicket' checked={!!activeTicket.isCompletedTicket} />
                    </label>
                    <label className={s.switchRow}>
                        <span>
                            <strong>Deleted</strong>
                            <small>Hide this ticket from queues.</small>
                        </span>
                        <FormCheck id='help-ticket-deleted' type='switch' onChange={e=> handleSwitch(e, e.target.checked)}
                            name='isDeletedTicket' checked={!!activeTicket.isDeletedTicket} />
                    </label>
                </div>
                : null}

                <div className={s.editorActions}>
                    <Button variant='light' onClick={() => dispatch(toggleTicketModal(false))}>
                        <FontAwesomeIcon name='times' />
                        <span>cancel</span>
                    </Button>
                    <Button variant='primary'
                        disabled={!activeTicket.description || !activeTicket.ticketType}
                        onClick={() => doSaveTicket()}>
                        <FontAwesomeIcon name='check' />
                        <span>save ticket</span>
                    </Button>
                </div>
            </section>

            {_isExistingTicket ?
            <section className={s.commentsPanel}>
                <div className={s.sectionHeader}>
                    <div>
                        <h3>Comments</h3>
                        <p>{_comments.length} {_comments.length === 1 ? 'comment' : 'comments'}</p>
                    </div>
                </div>
                {_comments.length === 0 ?
                    <Alert className={s.emptyState} small variant='info' icon='info-circle'
                        message='No comments on this ticket.' />
                : null}
                <ListGroup className={s.commentsList}>
                    {_comments.map(c =>
                        <ListGroup.Item key={c._id} className={s.commentItem}>
                            <Image className={s.commentAvatar}
                                src={getAvatarUrl((c.player || {}).avatarUrl)}
                                roundedCircle />
                            <div className={s.commentContent}>
                                <div className={s.commentHeader}>
                                    <h5>{(c.player || {}).fullName}</h5>
                                    {c.createDate && moment(c.createDate).isValid() ? <span>{moment(c.createDate).fromNow()}</span> : null}
                                </div>
                                <p>
                                    <Linkify properties={{ target: '_blank' }}>{c.comment}</Linkify>
                                </p>
                            </div>
                            <Confirm variant='danger' onConfirm={() => doDeleteComment(c._id)}
                                title={'Delete Comment'}
                                confirmText='delete comment'
                                body={'Are you sure you want to delete this comment?'}
                                size='sm' buttonClassName={s.deleteButton} buttonIcon='trash' />
                        </ListGroup.Item>
                    )}
                    <ListGroup.Item className={s.newCommentListItem}>
                        <TextareaAutosize name='Description'
                            placeholder='Add a comment...'
                            value={comment} className={'form-control ' + s.newCommentTextBox}
                            onKeyDown={e => handleKeyPress(e)}
                            onChange={e => handleCommentChange(e)} rows={1}
                            onResize={() => {}} />
                        <Button variant='primary' disabled={!comment || !comment.trim()} onClick={() => addCommentToTicket()}>
                            <FontAwesomeIcon name='paper-plane' />
                            <span>comment</span>
                        </Button>
                    </ListGroup.Item>
                </ListGroup>
            </section>
            : null}
        </div>
    )
}

export default EditTicket
